import React from 'react';
import { useEffect, useState } from "react";
import { Card, Typography, Avatar, Button, Box, CircularProgress, Tooltip, RadioGroup, Radio, Textarea, IconButton, Select, Option, useTheme } from "@mui/joy";
import { useAuth } from "../context/AuthContext";
import { Stack, useMediaQuery } from "@mui/material";
import { useLanguage } from "../LanguageProvider";
import Money from '../img/fluid-dollar-coin.png';
import { Wallet } from "@mui/icons-material";
import { getApiUrl } from '../utils/api';

function Income() {
    const { user, loading } = useAuth();
    const [models, setModels] = useState([]);
    const { currentLanguageData } = useLanguage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchModels = async () => {
            try {
                const response = await fetch(getApiUrl('/v3/ai/models/owned'), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${user.jwt_token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(`Error: ${response.statusText}`);
                }

                const data = await response.json();
                setModels(data);
            } catch (err) {
                console.log(err.message);
            }
        };

        fetchModels();
    }, []);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: ((window.__TAURI_INTERNALS__ && !isMobile) ? 'calc(100vh - 30px)' : '100vh') }}>
                <CircularProgress color='neutral' />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flex: 1, justifyContent: 'center' }}>
            <Stack sx={{
                width: '90%',
                marginTop: '90px',
                maxWidth: '740px',
            }}>
                <Card variant="soft" color="warning" sx={{ mb: 2, overflow: 'hidden' }}>
                    <Typography level="title-md">{currentLanguageData.monetizationSystem}</Typography>
                    <Typography level="body-sm" sx={{ mt: -0.5 }}>{currentLanguageData.description}<br />{currentLanguageData.flexibleRevenue}</Typography>
                    <Typography level="body-xs" sx={{ mt: -0.5 }}>{currentLanguageData.supportedCountries}</Typography>
                    <img style={{ pointerEvents: 'none', opacity: 0.3, position: 'absolute', right: 0, bottom: -40, height: '110%', objectFit: 'cover' }} src={Money} />
                </Card>
                <Card variant="soft" sx={{ mb: 2, overflow: 'hidden' }}>
                    <Typography level="title-md">{currentLanguageData.yourIncome}</Typography>
                    <Typography sx={{ mt: -1.0, fontWeight: '600' }} level="h2">{user?.ai?.balance ?? 0.00} USD</Typography>
                    <Typography sx={{ mt: -0.5 }} level="body-xs">{currentLanguageData.sinceLastWithdrawal}</Typography>
                </Card>
                <Card variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                    <Typography level="title-md">{currentLanguageData.linkToWallet}</Typography>
                    <Box sx={{ mt: -1.0 }}>
                        <Button startDecorator={<Wallet />} disabled variant="soft" sx={{ borderRadius: 250 }}>{currentLanguageData.receivePayments}</Button>
                    </Box>
                    <Typography sx={{ mt: -0.5 }} level="body-xs">{currentLanguageData.separateAccountInfo}</Typography>
                </Card>
                <Card variant="soft" sx={{ mb: 2, overflow: 'hidden' }}>
                    <Typography level="title-md">{currentLanguageData.yourStatistics}</Typography>

                    <Stack sx={{ mt: -0.5 }} spacing={1}>
                        {models.sort((a, b) => b.total_messages - a.total_messages).map((model, index) => (
                            <Card variant="plain" sx={{ borderRadius: 20 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Stack spacing={1} direction="row" sx={{ alignItems: 'center' }}>
                                        <Avatar src={model.avatar} />
                                        <Typography level="title-sm">{model.name}</Typography>
                                    </Stack>

                                    <Stack spacing={1} direction="row" sx={{ alignItems: 'center' }}>
                                        <Tooltip arrow placement="top" color="neutral" variant="outlined" title={currentLanguageData.regularMessages}>
                                            <Card variant="soft" sx={{ borderRadius: 20, px: 1, py: 0.5 }}>
                                                <Typography level="body-sm">{model.total_messages.toLocaleString()}</Typography>
                                            </Card>
                                        </Tooltip>
                                        <Tooltip arrow placement="top" color="warning" variant="outlined" title={currentLanguageData.paidMessages}>
                                            <Card invertedColors variant="soft" color="warning" sx={{ borderRadius: 20, px: 1, py: 0.5 }}>
                                                <Typography level="body-sm">{model.paid_messages.toLocaleString()}</Typography>
                                            </Card>
                                        </Tooltip>
                                    </Stack>
                                </Box>
                            </Card>
                        ))}
                    </Stack>
                    <Typography sx={{ mt: -0.5 }} level="body-xs">{currentLanguageData.personalTopBots}</Typography>
                </Card>
            </Stack>
        </div >
    );
}

export default Income;
